import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./PageContainer.css";
import { useData, useVersionData } from "../data/Data";
import { Card } from "./Card";
import { Category } from "../data/RowData";
import { SoftwareVersioning } from "../data/SoftwareVersioning";
import { useAppContext } from "../services/AppService";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Load release notes from local file that we made such as encore2.txt or etc
const ReleaseNotesPopover: React.FC<{ changes?: string }> = ({ changes }) => {
  const [releaseNotes, setReleaseNotes] = useState("Loading release notes...");

  useEffect(() => {
    if (!changes) {
      setReleaseNotes("No release notes available.");
      return;
    }

    // Pull from local file just like we create api routes in next
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch(`/changes/${changes}.txt`);

        if (!response.ok) {
          setReleaseNotes("Release notes unavailable.");
          return;
        }

        setReleaseNotes(await response.text());
      } catch (error) {
        console.error("Error fetching release notes:", error);
        setReleaseNotes("Error loading release notes.");
      }
    };

    fetchReleaseNotes();
  }, [changes]);

  return (
    <PopoverContent side="top" className="release-notes-popover">
      {releaseNotes}
    </PopoverContent>
  );
};

export const PageContainer: React.FC = () => {
  const versionData = useVersionData();
  const data = useData();
  const { state, setState } = useAppContext();

  const regex = useMemo(
    () => new RegExp(`.*${state.search.replace(/ /g, ".*")}.*`, "i"),
    [state.search]
  );

  const getDownloadCards = useCallback(
    () =>
      data
        .filter(
          (datum) =>
            state.category === Category.all || datum.category === state.category
        )
        .filter(
          (datum) => datum.name.match(regex) || datum.description.match(regex)
        )
        .map((i, key) => (
          <Card
            id={i.name
              .replace(/[^a-z0-9_]+/gi, "-")
              .replace(/^-|-$/g, "")
              .toLowerCase()}
            name={i.name}
            category={i.category}
            description={i.description}
            update={i.update}
            url={i.url}
            renderDescription={i.renderDescription}
            key={key}
          />
        )),
    [state, data, regex]
  );

  const groupedVersionData = useCallback((): SoftwareVersioning[][] => {
    const pairs: SoftwareVersioning[][] = [];
    for (let i = 0; i < versionData.length; i += 2) {
      pairs.push(versionData.slice(i, i + 2));
    }
    return pairs;
  }, [versionData]);

  const createTableDataEntry = (
    ver: SoftwareVersioning,
    globalMfg: "global-col" | "mfg-col"
  ) => (
    <td className={globalMfg}>
      <span className="firmware-number tooltip">
        {ver.number}
        <span className="tooltiptext">{ver.description}</span>
      </span>
      <i className="firmware-date">
        {" "}
        - {ver.versionDate.toLocaleDateString().replace(/\//g, "-")}
      </i>
    </td>
  );

  const getVersionInformation = useCallback(
    () =>
      groupedVersionData()
        .filter(
          (pair) =>
            pair[0].name.match(regex) ||
            pair[0].number.match(regex) ||
            pair[0].description.match(regex) ||
            pair[1].name.match(regex) ||
            pair[1].number.match(regex) ||
            pair[1].description.match(regex)
        )
        .map((pair, index) => (
          <tr key={index}>
            <td className="firmware firmware-col">{pair[0].name}</td>
            {createTableDataEntry(pair[0], "global-col")}
            {createTableDataEntry(pair[1], "mfg-col")}
            <td className="changelog">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="tooltip tooltiptext release-notes-trigger"
                    type="button"
                  >
                    Release Notes
                  </button>
                </PopoverTrigger>
                <ReleaseNotesPopover changes={pair[0].changes} />
              </Popover>
            </td>
          </tr>
        )),
        [groupedVersionData, regex]
      );

  const noResults = () => <div className="no-results">No results found</div>;

  return (
    <div onClick={() => setState("hamburgerMenuIsOpen", false)}>
      {state.category !== Category.ver ? (
        <div className="software">
          {getDownloadCards().length === 0 ? noResults() : getDownloadCards()}
        </div>
      ) : (
        <div className="page-container">
          {getVersionInformation().length === 0 ? (
            noResults()
          ) : (
            <table className="firmware-table">
              <thead>
                <tr className="titles">
                  <td className="firmware-col">Firmware</td>
                  <td className="global-col">Global Release</td>
                  <td className="mfg-col">Manufacturing Release</td>
                  <td className="changelog-col">{/* left empty for spacing on log*/}</td>
                </tr>
              </thead>
              <tbody>{getVersionInformation()}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
